import React, { useEffect, useMemo } from 'react';
import { getUvInsertConfigDescription } from '@shared/view/uv-inserts/get-config-description';
import { getUvInsertPriceRange } from '@shared/view/uv-inserts/get-price-display';
import { sanitiseVisualiserText } from '@uv-inserts/components/Visualiser/VisualiserTextBox/sanitise-text';
import { OptionSelectionPage } from '../../components/GiftWrapModal/InsertsPages/pages/OptionSelectionPage';
import {
  getPageOptions,
  pages,
} from '../../context/pages';
import { usePage } from '../../context/PageContext';
import { useUserInput } from '../../context/UserInputContext';
import { useUvInsertsContext } from '../context/UvInsertsContext';
import { useLocale } from '../../context/LocaleContext';
import { findInlineCard } from '../../packaging/inlineCard';
import {
  getOptionVisibilityForOptions,
  getReachableConfigHandlesForClicksTo,
  resolveSinglePathDestination,
  type OptionNode,
} from '../navigation/option-tree';

type OptionSelectionRouteProps = {
  pageName: string;
};

/**
 * TanStack route wrapper around OptionSelectionPage.
 *
 * Computes MVVM-sourced subtitle overrides for every option on the page:
 *
 * 1. Finds all visualiser-route configs reachable from each option's clicksTo destination
 *    (works for both direct leaf routes and multi-step category routes).
 * 2. Derives a base description:
 *    - Single reachable config → uses config.description from uv-inserts-config.liquid via MVVM.
 *    - Multiple configs (category option) → uses the static subtitle from pages.js.
 * 3. Appends the highest price across the reachable theme variants active for this piece's segment
 *    (priceRanges, via the pure @shared/view getUvInsertPriceRange; no window access), always as
 *    "(£5.99)": never "From" (ALBAU-894, client decision). The highest, so a path that also holds a
 *    cheaper card (e.g. the £4.99 gold-foil card on Men's) never shows less than the main card costs.
 * 4. Formats prices with the gift-wrap LocaleContext formatPrice.
 *
 * No window.giftWrapData reads at any layer.
 */
export function OptionSelectionRoute({ pageName }: OptionSelectionRouteProps) {
  const { resolved } = useUvInsertsContext();
  const { formatPrice } = useLocale();
  const { goToPage, getPageSelection, inlineCardMessage, setInlineCardMessage } = usePage();
  const { setVisualiserSelection, clearVisualiserSelection } = useUserInput();
  const options = useMemo(() => getPageOptions(pageName) as (OptionNode & { subtitle?: string })[], [pageName]);
  const configByHandle = useMemo<Map<string, (typeof resolved.configs)[number]>>(() => {
    if (!resolved) return new Map();
    return new Map(resolved.configs.map((config) => [config.handle, config]));
  }, [resolved]);

  const pageConfig = (pages as Record<string, { autoAdvance?: boolean }>)[pageName];
  // Prices only count configs active for this piece's segment (Men's and Women's inserts are priced differently).
  const activeConfigs = useMemo(() => (resolved ? resolved.configs.filter((config) => config.active) : []), [resolved]);

  const priceRanges = useMemo(() => {
    const ranges: Record<number, { minPounds: number; maxPounds: number }> = {};
    options.forEach((option, index) => {
      const configHandles = getReachableConfigHandlesForClicksTo(option?.clicksTo);
      const range = configHandles.length > 0 ? getUvInsertPriceRange({ configs: activeConfigs, configHandles }) : null;
      if (range) ranges[index] = range;
    });
    return ranges;
  }, [options, activeConfigs]);

  const subtitleOverrides = useMemo<Record<number, string>>(() => {
    if (!resolved) return {};

    const overrides: Record<number, string> = {};

    options.forEach((option, index) => {
      const configHandles = getReachableConfigHandlesForClicksTo(option?.clicksTo);

      if (configHandles.length === 0) return;

      // Base description text:
      //   - One config → MVVM config.description (declared in uv-inserts-config.liquid).
      //   - Many configs → fall back to the static subtitle in pages.js (category-level copy).
      const mvvmDescription =
        configHandles.length === 1
          ? getUvInsertConfigDescription({ configHandle: configHandles[0], resolved })
          : null;

      const baseText: string = mvvmDescription ?? (option?.subtitle as string | undefined) ?? '';

      // The same active-segment price as the step 3 cards.
      const range = priceRanges[index];

      if (!baseText && !range) return;

      // Format price using the gift-wrap locale context (handles currency symbol + locale).
      const priceStr = range ? formatPrice({ amount: range.maxPounds }) : null;

      const subtitle = baseText
        ? priceStr
          ? `${baseText} (${priceStr})`
          : baseText
        : priceStr ?? '';

      if (subtitle) {
        overrides[index] = subtitle;
      }
    });

    return overrides;
  }, [resolved, formatPrice, options, priceRanges]);

  const optionVisibility = useMemo(() => {
    if (!resolved) {
      return {
        hiddenOptionIndexes: new Set<number>(),
        visibleOptionIndexes: options.map((_, index) => index),
      };
    }
    return getOptionVisibilityForOptions(options, configByHandle);
  }, [resolved, options, configByHandle]);

  // Packaging step 3: the Merry Christmas card takes its message on the card when a Christmas design exists.
  const inlineCard = useMemo(
    () => (pageName === 'addPersonalTouch' ? findInlineCard({ options, resolved }) : null),
    [pageName, options, resolved]
  );
  const inlineCardSelected = inlineCard !== null && getPageSelection(pageName) === inlineCard.optionIndex;

  // Price the selected card as the picker does (footer total and View Summary read visualiserSelection).
  useEffect(() => {
    if (!inlineCard || !inlineCardSelected) return undefined;
    setVisualiserSelection({ productKey: inlineCard.productKey, variant: inlineCard.variant, config: null });
    return () => clearVisualiserSelection();
  }, [inlineCard, inlineCardSelected, setVisualiserSelection, clearVisualiserSelection]);

  const messageCard = useMemo(
    () =>
      inlineCard && {
        optionIndex: inlineCard.optionIndex,
        maxLength: inlineCard.maxLength,
        value: inlineCardMessage,
        onChange: setInlineCardMessage,
        clean: (text: string) => sanitiseVisualiserText(text, inlineCard)
      },
    [inlineCard, inlineCardMessage, setInlineCardMessage]
  );

  const singlePathDestination = useMemo(() => {
    if (!resolved || pageConfig?.autoAdvance === false) return null;
    return resolveSinglePathDestination({ pageName, configByHandle });
  }, [resolved, pageName, configByHandle, pageConfig]);

  useEffect(() => {
    if (!singlePathDestination) return;
    goToPage(singlePathDestination);
  }, [singlePathDestination, goToPage]);

  if (singlePathDestination) {
    return null;
  }

  return (
    <OptionSelectionPage
      pageName={pageName}
      subtitleOverrides={subtitleOverrides}
      hiddenOptionIndexes={optionVisibility.hiddenOptionIndexes}
      priceRanges={priceRanges}
      messageCard={messageCard}
    />
  );
}
